// app/dealer-tips/page.jsx
"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Calendar, Plus } from "lucide-react";
import { toast } from "react-hot-toast";
import moment, { parseAppDateTime, formatInAppTz, isDateTimeInRange } from "../utils/timezone";
import { employeeManagementService } from '../apis/employeeManagement';
import { dealertipoutService } from "../apis/dealertipoutService";
import DateRangePicker from "../components/ui/date-range-picker";
import { useAuth } from "../context/AuthContext";
import { DealerTipAddModal } from "../components/dealer-tip-add-modal";
import { DealerTipEditModal } from "../components/dealer-tip-edit-modal";
import { DealerTipDeleteModal } from "../components/dealer-tip-delete-modal";
import { DealerTipsTransactionTable } from "../components/dealer-tips-transaction-table";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { OverallTotalsTable } from "../components/overall-total-tables";
import { calculateTipCommission, TIP_ROLES } from "../utils/tipCommission";
import { base64Logo } from '../constants/pdfLogo'

const calculateCommission = (amount, transactionDate = new Date()) =>
    calculateTipCommission(amount, TIP_ROLES.DEALER, transactionDate);

export default function DealerTipsPage() {
    const { currentUser } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [records, setRecords] = useState([]);
    const [allemployees,setallemployees]=useState([]);

    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);

    // Filter states
    const [dateRange, setDateRange] = useState({
        start: moment().subtract(1, "month").toDate(),
        end: new Date()
    });
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [refreshFlag, setRefreshFlag] = useState(false);
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);

    // Overall totals state
    const [overallTotals, setOverallTotals] = useState({
        balance: null,
        employeeTotals: []
    });

    const [overallTotalsLoading, setOverallTotalsLoading] = useState(false);


    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const response = await employeeManagementService.getAllEmployees({ limit: 1000000 });
                if (response.success) {
                    setallemployees(response?.data?.body?.data?.employees);
                }
            } catch (error) {
                console.error('Error fetching employees:', error);
            }
        };

        fetchEmployees();
    }, []);

    // Fetch overall totals function
    const fetchOverallTotals = async (start, end) => {
        try {
            setOverallTotalsLoading(true);
            const response = await dealertipoutService.getOverallTotals(
                start.toISOString(),
                end.toISOString()
            );
            if (response?.employeeTotals) {

                setOverallTotals({
                    employeeTotals: response?.employeeTotals || []
                });
            } else {
                toast.error("Failed to load overall totals");
            }
        } catch (err) {
            console.error("Error fetching overall totals:", err);
            toast.error("Failed to load overall totals");
        } finally {
            setOverallTotalsLoading(false);
        }
    };

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true);

            const [balancesRes, recordsRes] = await Promise.all([
                dealertipoutService.getBalances(),
                dealertipoutService.getRecords(),
            ]);

            // Handle records response - ensure it's always an array
            if (recordsRes.success && Array.isArray(recordsRes.data)) {
                setRecords(recordsRes.data);
            } else {
                console.warn("Records response is not an array:", recordsRes);
                setRecords([]); // Fallback to empty array
            }

            // Handle balances if needed (you might want to use this data)
            if (balancesRes.success) {
                        console.log("Balances loaded:", balancesRes.data);
            }

        } catch (err) {
            console.error("Error fetching data:", err);
            setError("Failed to load data");
            toast.error("Failed to load dealer tips data");
            // Ensure arrays are still set even on error
            setRecords([]);
        } finally {
            setIsLoading(false);
        }
    }, []);


    useEffect(() => {
        fetchData();
        fetchOverallTotals(dateRange.start, dateRange.end);
    }, [fetchData, refreshFlag, dateRange.start, dateRange.end]);

    // Add new useEffect for date range changes
    useEffect(() => {
        fetchOverallTotals(dateRange.start, dateRange.end);
    }, [dateRange.start, dateRange.end]);


    // Handle adding a new record
    const handleAddRecord = async ({ empname, amount, shift }) => {
        try {
            setIsLoading(true);

            const response = await dealertipoutService.addRecord({
                empname,
                amountcash: amount,
                shift,
            });

            if (response.success) {
                toast.success("Record added successfully");
                // Ensure we're working with an array
                setRecords(prev => Array.isArray(prev) ? [response.data, ...prev] : [response.data]);
                setShowAddModal(false);
                setRefreshFlag(prev => !prev);
            } else {
                toast.error(response.error || "Failed to add record");
            }
        } catch (err) {
            console.error("Error adding record:", err);
            toast.error("An error occurred");
        } finally {
            setIsLoading(false);
        }
    };

    // Handle record update
    const handleUpdateRecord = async ({ id, date, empname, amount }) => {
        try {
            setIsLoading(true);

            const response = await dealertipoutService.updateRecord(id, {
                date: parseAppDateTime(date)?.format("YYYY-MM-DDTHH:mm:ss") ?? date,
                empname,
                amountcash: amount
            });

            if (response.success) {
                toast.success("Record updated successfully");

                // Update records - ensure we're working with an array
                setRecords(prev =>
                    Array.isArray(prev)
                        ? prev.map(rec => rec.id === id ? response.data : rec)
                        : [response.data]
                );
                setRefreshFlag(prev => !prev);
                setShowEditModal(false);


            } else {
                toast.error(response.error || "Failed to update record");
            }
        } catch (err) {
            console.error("Error updating record:", err);
            toast.error("An error occurred");
        } finally {
            setIsLoading(false);
        }
    };

    // Handle record deletion
    const handleDeleteRecord = async () => {
        if (!currentRecord) return;

        try {
            setIsLoading(true);
            const { id } = currentRecord;

            const response = await dealertipoutService.deleteRecord(id);

            if (response.success) {
                toast.success("Record deleted successfully");

                // Remove from records - ensure we're working with an array
                setRecords(prev =>
                    Array.isArray(prev)
                        ? prev.filter(rec => rec.id !== id)
                        : []
                );
                setRefreshFlag(prev => !prev);
                setShowDeleteModal(false);
            } else {
                toast.error(response.error || "Failed to delete record");
            }
        } catch (err) {
            console.error("Error deleting record:", err);
            toast.error("An error occurred");
        } finally {
            setIsLoading(false);
        }
    };

    // Replace the filteredRecords useMemo with this fixed version:

    const filteredRecords = useMemo(() => {
        // Ensure records is always an array before filtering
        if (!Array.isArray(records)) {
            console.warn("Records is not an array:", records);
            return [];
        }

        return records.filter(record => {
            const inDateRange = isDateTimeInRange(record.date, dateRange.start, dateRange.end);

            const matchesSearch =
                !searchTerm ||
                (record.employee && record.employee.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (record.added_by && record.added_by.toLowerCase().includes(searchTerm.toLowerCase()));

            return inDateRange && matchesSearch;
        });
    }, [records, dateRange, searchTerm]);

    // Format date range for display
    const formatDateRange = () => {
        return `${moment(dateRange.start).format("MM/DD/YYYY")} - ${moment(dateRange.end).format("MM/DD/YYYY")}`;
    };

    // Handle edit record
    const handleEditRecord = (record) => {
        setCurrentRecord(record);
        setShowEditModal(true);
    };

    // Handle delete record
    const handleDeleteRecordClick = (record) => {
        setCurrentRecord(record);
        setShowDeleteModal(true);
    };

    const summaryCalculations = useMemo(() => {
        // Ensure filteredRecords is an array
        if (!Array.isArray(filteredRecords)) {
            return { tipPool: 0, addedTokes: 0, paidOut: 0 };
        }

        return filteredRecords.reduce(
            (acc, record) => {
                const tipOutCash = parseFloat(record.tip_out_cash) || 0;
                const tax = parseFloat(record.tax) || 0;
                const paidOutAmount = tipOutCash - tax;

                return {
                    tipPool: acc.tipPool + tipOutCash,
                    addedTokes: acc.addedTokes + tax,
                    paidOut: acc.paidOut + paidOutAmount
                };
            },
            { tipPool: 0, addedTokes: 0, paidOut: 0 }
        );
    }, [filteredRecords]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-[50vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center min-h-[50vh]">
                <div className="text-red-500 text-xl">{error}</div>
            </div>
        );
    }

    const formatCurrency = (value) => {
        return parseFloat(value).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    // Overall Totals Table Component


    const exportDealerTipsRecordsToPDF = () => {
        try {
            const doc = new jsPDF();

            // Set document properties
            doc.setProperties({
                title: 'Dealer Tips Report',
                subject: 'Dealer Tips Transactions',
                author: 'Elite Poker Lounge',
                creator: 'Dealer Tips System'
            });

            // Add logo (if available)
            if (base64Logo) {
                const logoWidth = 40;
                const logoHeight = 20;
                doc.addImage(base64Logo, 'PNG', 150, 10, logoWidth, logoHeight);
            }

            // Add timestamp
            const now = new Date();
            const dateTime = now.toLocaleString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });

            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Date & Time: ${dateTime}`, 14, 15);
            doc.text(`Date Range: ${formatDateRange()}`, 14, 25);

            // Title
            doc.setFontSize(18);
            doc.setTextColor(0);
            doc.setFont(undefined, 'bold');
            doc.text('Dealer Tips Records', 14, 40);

            // Summary section
            doc.setFontSize(12);
            doc.setTextColor(0);
            doc.setFont(undefined, 'bold');
            doc.text('Period Summary:', 14, 50);

            doc.setFont(undefined, 'normal');
            doc.text(`Total Tip Pool: $${formatCurrency(summaryCalculations.tipPool)}`, 14, 60);
            doc.text(`Total Added Tokes: $${formatCurrency(summaryCalculations.addedTokes)}`, 14, 70);
            doc.text(`Total Paid Out: $${formatCurrency(summaryCalculations.paidOut)}`, 14, 80);

            // Overall Totals Section
            if (overallTotals.balance) {
                doc.setFontSize(12);
                doc.setFont(undefined, 'bold');
                doc.text('Overall Balance:', 14, 95);

                doc.setFont(undefined, 'normal');
                doc.text(`Total Balance: $${formatCurrency(overallTotals.balance.total || 0)}`, 14, 105);
                doc.text(`Tax Collections: $${formatCurrency(overallTotals.balance.tax_collections || 0)}`, 14, 115);
                doc.text(`Total Tokes: $${formatCurrency(overallTotals.balance.tokes || 0)}`, 14, 125);
            }

            let currentY = 140;

            // Employee Totals Section
            if (overallTotals.employeeTotals && overallTotals.employeeTotals.length > 0) {
                doc.setFontSize(12);
                doc.setFont(undefined, 'bold');
                doc.text('Employee Totals:', 14, currentY);

                // Employee totals table
                const employeeHeaders = ['Employee', 'Total Tip Out', 'Total Tax', 'Total Paid Out'];
                const employeeData = overallTotals.employeeTotals.map(employee => [
                    employee.employee,
                    `$${formatCurrency(employee.tipOut || 0)}`,
                    `$${formatCurrency(employee.tax || 0)}`,
                    `$${formatCurrency(employee.paidOut || 0)}`
                ]);

                // Add employee totals row
                employeeData.push([
                    'TOTAL',
                    `$${formatCurrency(overallTotals.employeeTotals.reduce((sum, emp) => sum + (emp.tipOut || 0), 0))}`,
                    `$${formatCurrency(overallTotals.employeeTotals.reduce((sum, emp) => sum + (emp.tax || 0), 0))}`,
                    `$${formatCurrency(overallTotals.employeeTotals.reduce((sum, emp) => sum + (emp.paidOut || 0), 0))}`
                ]);

                autoTable(doc, {
                    head: [employeeHeaders],
                    body: employeeData,
                    startY: currentY + 5,
                    theme: 'plain',
                    styles: {
                        fontSize: 9,
                        cellPadding: 3,
                        textColor: [0, 0, 0],
                        lineColor: [0, 0, 0],
                        lineWidth: 0.1
                    },
                    headStyles: {
                        fillColor: false,
                        textColor: [0, 0, 0],
                        fontStyle: 'bold',
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0]
                    },
                    bodyStyles: {
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0]
                    },
                    columnStyles: {
                        0: { cellWidth: 40 },
                        1: { cellWidth: 35, halign: 'right' },
                        2: { cellWidth: 35, halign: 'right' },
                        3: { cellWidth: 35, halign: 'right' }
                    },
                    margin: { left: 14, right: 14 },
                    tableLineColor: [0, 0, 0],
                    tableLineWidth: 0.1,
                    didParseCell: function (data) {
                        // Make the total row bold
                        if (data.row.index === employeeData.length - 1) {
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }
                });

                currentY = doc.lastAutoTable.finalY + 15;
            }

            // Transaction Details Section
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text('Transaction Details:', 14, currentY);

            // Prepare transaction table data
            const transactionHeaders = [
                'Date',
                'Employee',
                'Tip Out Cash',
                'Tax',
                'Paid Out',
                'Added By'
            ];

            const transactionData = filteredRecords.map(record => [
                parseAppDateTime(record.date)?.format("MM/DD/YYYY hh:mm A") ?? formatInAppTz(record.date),
                record.employee || 'N/A',
                `$${formatCurrency(record.tip_out_cash || 0)}`,
                `$${formatCurrency(record.tax || 0)}`,
                `$${formatCurrency((parseFloat(record.tip_out_cash) || 0) - (parseFloat(record.tax) || 0))}`,
                record.added_by || 'N/A'
            ]);

            // Add transaction details table
            autoTable(doc, {
                head: [transactionHeaders],
                body: transactionData,
                startY: currentY + 5,
                theme: 'plain',
                styles: {
                    fontSize: 8,
                    cellPadding: 2,
                    textColor: [0, 0, 0],
                    lineColor: [0, 0, 0],
                    lineWidth: 0.1
                },
                headStyles: {
                    fillColor: false,
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                    lineWidth: 0.1,
                    lineColor: [0, 0, 0]
                },
                columnStyles: {
                    0: { cellWidth: 35 },
                    1: { cellWidth: 30 },
                    2: { cellWidth: 25, halign: 'right' },
                    3: { cellWidth: 25, halign: 'right' },
                    4: { cellWidth: 25, halign: 'right' },
                    5: { cellWidth: 30 }
                },
                margin: { left: 14, right: 14 },
                tableLineColor: [0, 0, 0],
                tableLineWidth: 0.1
            });

            // Save the PDF
            const fileName = `Dealer_Tips_Report_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}.pdf`;
            doc.save(fileName);

            toast.success('PDF exported successfully!');
        } catch (error) {
            console.error('Error generating PDF:', error);
            toast.error('Error generating PDF. Please try again.');
        }
    };


    return (
        <div className="bg-gray-50 p-6">
            {/* Header */}
            <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-800">Dealer Tips Records</h1>
                <p className="text-gray-600 mt-2">Manage and track dealer tip distributions</p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <Card className="bg-white shadow-sm border border-gray-200">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">Tip Pool</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-red-600">${formatCurrency(summaryCalculations.tipPool)}</div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-white shadow-sm border border-gray-200">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">Added Tokes</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-red-600">${formatCurrency(summaryCalculations.addedTokes)}</div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-white shadow-sm border border-gray-200">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">Paid Out</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">${formatCurrency(summaryCalculations.paidOut)}</div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Add Record Button */}
            <div className="flex justify-end mb-6">
                <Button
                    onClick={() => setShowAddModal(true)}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                    <Plus className="mr-2 h-4 w-4" /> Add Record
                </Button>
            </div>

            {/* Filter Controls */}
            <div className="bg-white p-4 rounded-lg shadow mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                        <Button
                            variant="outline"
                            onClick={() => setIsDatePickerOpen(true)}
                            className="w-full justify-start text-left font-normal"
                        >
                            <Calendar className="mr-2 h-4 w-4" />
                            {formatDateRange()}
                        </Button>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                        <Input
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search records..."
                        />
                    </div>
                </div>
            </div>

            {/* Records Table */}
            <Card className="bg-white shadow rounded-lg mb-8">
                <CardHeader>
                    <div className="flex justify-between items-center">
                        <CardTitle className="text-lg font-semibold text-gray-800">Transaction History</CardTitle>
                        <Button
                            onClick={exportDealerTipsRecordsToPDF}
                            variant="outline"
                            className="bg-yellow-400 hover:bg-yellow-500 text-black"
                        >
                            Export to PDF
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    <DealerTipsTransactionTable
                        records={filteredRecords}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        entriesPerPage={entriesPerPage}
                        setEntriesPerPage={setEntriesPerPage}
                        onEdit={handleEditRecord}
                        onDelete={handleDeleteRecordClick}
                        currentUser={currentUser}
                        dateRange={dateRange}
                        employeeApiService={employeeManagementService}
                        employees={allemployees}
                    />
                </CardContent>
            </Card>

            {/* Overall Totals Section */}
            <Card className="bg-white shadow rounded-lg">
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg font-semibold text-gray-800">Overall Total Tips Received</CardTitle>
                    <Button
                        onClick={() => fetchOverallTotals(dateRange.start, dateRange.end)}
                        variant="outline"
                        size="sm"
                        disabled={overallTotalsLoading}
                    >
                        {overallTotalsLoading ? "Refreshing..." : "Refresh"}
                    </Button>
                </CardHeader>
                <CardContent>
                    <OverallTotalsTable
                    overallTotals={overallTotals}
                    dateRange={dateRange}
                    employeeApiService={employeeManagementService}
                    employees={allemployees}
                    />
                </CardContent>
            </Card>

            {/* Modals */}
            <DealerTipAddModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onAdd={handleAddRecord}
                employees={allemployees}
                calculateCommission={calculateCommission}
            />

            <DealerTipEditModal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                record={currentRecord}
                onUpdate={handleUpdateRecord}
                employees={allemployees}
                calculateCommission={calculateCommission}
                currentUser={currentUser}
            />

            <DealerTipDeleteModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                record={currentRecord}
                onDelete={handleDeleteRecord}
            />

            {/* Date Range Picker */}
            <DateRangePicker
                isOpen={isDatePickerOpen}
                onClose={() => setIsDatePickerOpen(false)}
                onApply={(start, end) => {
                    setDateRange({ start, end });
                    setIsDatePickerOpen(false);
                }}
                initialStartDate={dateRange.start}
                initialEndDate={dateRange.end}
            />
        </div>
    );
}